import {type AccountType, providerChannels} from "@hosttools/core/constant";
import {Alert, Button, ButtonSpinner, ButtonText, useMobileView, VStack} from "@hosttools/frontend";
import {buildChannel} from "@hosttools/frontend/models/channel";
import type {FC} from "react";
import {useEffect, useMemo, useState} from "react";

import type {CredentialsType, Errors} from "./types";

import AirbnbCredential from "@/client/components/ModalAddAccount/AirbnbCredential";
import AugustCredential from "@/client/components/ModalAddAccount/AugustCredential";
import BookingCredential from "@/client/components/ModalAddAccount/BookingCredential";
import ChannelHeader from "@/client/components/ModalAddAccount/ChannelHeader";
import HomeAwayCredential from "@/client/components/ModalAddAccount/HomeAwayCredential";
import HostToolPolicy from "@/client/components/ModalAddAccount/HosttoolPolicy";
import HoufyCredential from "@/client/components/ModalAddAccount/HoufyCredential";
import SeamCredential from "@/client/components/ModalAddAccount/SeamCredential";
import Paragraph from "@/client/components/Paragraph";

interface Props {
    channel: AccountType;
    credentials: CredentialsType;
    info: string;
    errors: Errors;
    showSpinner: boolean;
    buttonText?: string;
    onChange: (credentials: CredentialsType) => void;
    onSubmitCredentials: () => void;
}

type FormValues = Omit<CredentialsType, "channel">;

const Credentials: FC<Props> = props => {
    const {
        channel: channelType,
        credentials: initCredentials = {
            username: "",
            password: ""
        },
        errors: propErrors,
        info = "",
        showSpinner,
        buttonText = "Add Account",
        onChange,
        onSubmitCredentials
    } = props;
    const isMobile = useMobileView();

    const {value} = useMemo(() => buildChannel(channelType), [channelType]);
    const [credentials, setCredentials] = useState<CredentialsType>({
        channel: channelType,
        username: initCredentials.username ?? "",
        password: ""
    });

    const [errors, setErrors] = useState<Errors>(propErrors);

    useEffect(() => {
        setErrors(propErrors);
    }, [propErrors]);

    function handleChangeCredentials<K extends keyof FormValues>(
        field: K,
        value: Required<FormValues>[K]
    ) {
        const newCredentials: Partial<FormValues> = {};
        if (field === "username") {
            newCredentials[field] = value.trim();
        } else {
            newCredentials[field] = value;
        }
        setCredentials(credentials => {
            return {...credentials, ...newCredentials};
        });
        onChange({
            ...newCredentials,
            channel: channelType
        });
    }

    const {label} = useMemo(() => buildChannel(value), [value]);

    return (
        <VStack className="h-[100%] md:h-auto" space="lg">
            <ChannelHeader channel={value} />
            {providerChannels.includes(value) && <HostToolPolicy />}
            {value === "Booking" && (
                <Paragraph variant="sm">
                    {"You can find your Booking.com property ID in your "}
                    <a href="https://admin.booking.com/" rel="noreferrer" target="_blank">
                        account settings page
                    </a>
                    {" on Booking.com."}
                </Paragraph>
            )}
            {value === "Houfy" && (
                <Paragraph variant="sm">
                    {"You can find your Houfy.com 'API Access Token' in your "}
                    <a
                        href="https://www.houfy.com/account/settings?tab=2"
                        rel="noreferrer"
                        target="_blank"
                    >
                        account settings page
                    </a>
                    {" on Houfy.com."}
                </Paragraph>
            )}
            <VStack space="lg">
                {value === "HomeAway" && (
                    <HomeAwayCredential
                        credentials={credentials}
                        errors={errors}
                        onChangeCredentials={handleChangeCredentials}
                    />
                )}

                {value === "Airbnb" && <AirbnbCredential />}

                {value === "Booking" && (
                    <BookingCredential
                        credentials={credentials}
                        errors={errors}
                        onChangeCredentials={handleChangeCredentials}
                    />
                )}

                {value === "Houfy" && (
                    <HoufyCredential
                        credentials={credentials}
                        errors={errors}
                        onChangeCredentials={handleChangeCredentials}
                    />
                )}

                {value === "August" && (
                    <AugustCredential
                        credentials={credentials}
                        errors={errors}
                        onChangeCredentials={handleChangeCredentials}
                    />
                )}

                {value === "Seam" && <SeamCredential />}

                {showSpinner && (
                    <Alert variant="subtle">
                        Connecting...
                        <br />
                        Host Tools is connecting to {label}. This process can take a few minutes.
                        Please be patient.
                    </Alert>
                )}

                {!!info && <Alert variant="subtle">{info}</Alert>}

                {!!errors?.error && (
                    <Alert action="error" variant="subtle">
                        {errors?.error}
                    </Alert>
                )}
                {errors?.errorCode === "SETUP_2FA" && (
                    <Alert action="error" variant="subtle">
                        {"Please setup "}
                        <a
                            href="https://help.vrbo.com/articles/How-do-I-set-up-two-factor-authentication"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            two-factor authentication
                        </a>
                        {
                            " on VRBO before connecting your account to Host Tools.  Here is a help article on VRBO.com on "
                        }
                        <a
                            href="https://help.vrbo.com/articles/How-do-I-set-up-two-factor-authentication"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            How do I set up two-factor authentication?
                        </a>
                    </Alert>
                )}
                {errors?.errorCode === "ACCOUNT_LOCKED" && (
                    <Alert action="error" variant="subtle">
                        {"Your VRBO account has run out of available sessions.  Please visit the "}
                        <a
                            href="https://www.vrbo.com/account-security"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            Privacy and Security
                        </a>
                        {
                            " page on VRBO and clear any sessions that have not been active in the last few days.  Then try connecting again.  If that doesn't work, please contact VRBO support by calling (877) 202-9331 and ask them to clear your sessions.  To avoid confusing the support rep, we recommend you tell them you're having issues logging into the mobile app."
                        }
                    </Alert>
                )}
                {isMobile && (
                    <Button className="w-full" onPress={onSubmitCredentials}>
                        {showSpinner && <ButtonSpinner />}
                        <ButtonText>{buttonText}</ButtonText>
                    </Button>
                )}
            </VStack>
        </VStack>
    );
};

export default Credentials;
